import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ENV_MATRIX_CSV_URL, getEnvMatrix, type EnvMatrix } from "@/lib/api";
import {
  CUSTOM_TABLE,
  filterRows,
  issuesByTable,
  keysWithIssues,
  tableOf,
  withCustomTable,
  type MatrixFilter,
} from "@/lib/envMatrix";
import { timeAgo } from "@/lib/deployments";
import { Modal } from "@/components/Modal";
import { MatrixTable, cellDomId, type CellRef, type RowRef } from "@/components/env-matrix/MatrixGrid";
import { IssuesList } from "@/components/env-matrix/IssuesList";
import { KeyDetails } from "@/components/env-matrix/KeyDetails";
import { TINT } from "@/components/env-matrix/tints";
import { RevealProvider } from "@/components/env-matrix/reveal";
import { CustomPicker } from "@/components/env-matrix/CustomPicker";

const NO_FILTER: MatrixFilter = { query: "", issuesOnly: false, differOnly: false };

/**
 * The + chip's selection lives in this tab's session storage: it survives
 * moving around the portal, goes when the tab does, and never reaches the
 * server. Storage can be unavailable (private windows), so both directions
 * fail quietly.
 */
const CUSTOM_KEY = "vp-env-matrix-custom";
function readCustom(): string[] | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(CUSTOM_KEY) ?? "null");
    return Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === "string") ? v : null;
  } catch {
    return null;
  }
}
function writeCustom(ids: string[] | null) {
  try {
    if (ids) sessionStorage.setItem(CUSTOM_KEY, JSON.stringify(ids));
    else sessionStorage.removeItem(CUSTOM_KEY);
  } catch {
    /* storage unavailable: the selection just won't outlive the page */
  }
}

export function EnvMatrixPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["env-matrix"],
    queryFn: () => getEnvMatrix(),
  });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [issuesOpen, setIssuesOpen] = useState(false);

  // Refresh asks the server to skip its one-minute cache and re-read Render.
  async function refresh() {
    setRefreshing(true);
    setRefreshError(null);
    try {
      qc.setQueryData(["env-matrix"], await getEnvMatrix(true));
    } catch (e) {
      setRefreshError((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="page-title">Env vars</h1>
          <p className="mt-2 max-w-2xl text-text-secondary">
            Every environment variable on every Render service, with the cross-service rules
            checked. Secrets stay hidden until you hover one. The = or ≠ beside each key says
            whether the services in the table share its value.
          </p>
        </div>
        {data?.configured && (
          <div className="flex flex-wrap items-center gap-2">
            <IssuesButton m={data} onClick={() => setIssuesOpen(true)} />
            <a
              href={ENV_MATRIX_CSV_URL}
              className="whitespace-nowrap rounded-token-md border border-border bg-bg-card px-4 py-2 text-sm font-medium text-text-heading transition-colors hover:bg-bg-card-hover"
            >
              <i className="fa-solid fa-file-csv mr-1.5" />
              Export CSV
            </a>
            <button
              type="button"
              onClick={refresh}
              disabled={refreshing}
              className="whitespace-nowrap rounded-token-md bg-accent px-4 py-2 text-sm font-semibold text-on-accent transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              <i className={`fa-solid fa-rotate mr-1.5 ${refreshing ? "fa-spin" : ""}`} />
              {refreshing ? "Refreshing" : "Refresh"}
            </button>
          </div>
        )}
      </header>

      {isLoading && (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      )}

      {(error || refreshError) && (
        <p className={`mb-4 rounded-token-lg border px-4 py-3 text-sm text-text ${TINT.dangerBorder} ${TINT.danger}`}>
          Couldn't read the env vars: {refreshError ?? (error as Error).message}
        </p>
      )}

      {data && !data.configured && <NotConfigured missing={data.missing} />}
      {data?.configured && (
        <MatrixView m={data} issuesOpen={issuesOpen} onIssuesOpen={setIssuesOpen} />
      )}
    </div>
  );
}

/** Opens the issues. Red when something is broken, amber for notices only. */
function IssuesButton({ m, onClick }: { m: EnvMatrix; onClick: () => void }) {
  const count = m.issues.length;
  const urgent = m.issues.some((i) => i.kind !== "notice");
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 whitespace-nowrap rounded-token-md border border-border bg-bg-card px-4 py-2 text-sm font-medium text-text-heading transition-colors hover:bg-bg-card-hover"
    >
      <i className="fa-solid fa-triangle-exclamation" />
      Issues
      {count > 0 && (
        <span
          className={`rounded-full px-1.5 font-mono text-xs ${
            urgent ? `text-danger ${TINT.danger}` : `text-warning ${TINT.warning}`
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function MatrixView({
  m,
  issuesOpen,
  onIssuesOpen,
}: {
  m: EnvMatrix;
  issuesOpen: boolean;
  onIssuesOpen: (open: boolean) => void;
}) {
  const [filter, setFilter] = useState<MatrixFilter>(NO_FILTER);
  /** Highlighted in the table, by "Show in table". */
  const [selected, setSelected] = useState<CellRef | null>(null);
  /** The key open in the details modal. */
  const [openKey, setOpenKey] = useState<RowRef | null>(null);
  const [scrollTo, setScrollTo] = useState<CellRef | null>(null);

  // A short message after a copy, the way the documents page confirms one.
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // The viewer's own comparison, from the + chip, added as one more table.
  const [custom, setCustomState] = useState<string[] | null>(readCustom);
  const [pickerOpen, setPickerOpen] = useState(false);
  const setCustom = (ids: string[] | null) => {
    setCustomState(ids);
    writeCustom(ids);
  };
  const view = useMemo(() => withCustomTable(m, custom), [m, custom]);

  // One table at a time. The first — the main services — is the default; any
  // other is kept in the address (?table=Keycloak), so a link opens on it.
  const [params, setParams] = useSearchParams();
  const defaultTable = view.tables[0]?.name ?? "";
  const asked = params.get("table");
  const active = asked && view.tables.some((t) => t.name === asked) ? asked : defaultTable;
  const activeIds = useMemo(
    () => view.tables.find((t) => t.name === active)?.serviceIds ?? [],
    [view.tables, active],
  );

  const issueKeys = useMemo(() => keysWithIssues(m.issues), [m.issues]);
  const rows = useMemo(
    () => filterRows(m.rows, filter, issueKeys, activeIds),
    [m.rows, filter, issueKeys, activeIds],
  );

  const broken = m.issues.filter((i) => i.kind === "rule").length;
  const notices = m.issues.filter((i) => i.kind === "notice").length;
  const exists = <T extends RowRef>(at: T | null) =>
    at && m.rows.some((r) => r.key === at.key && r.kind === at.kind) ? at : null;
  const showCell = exists(selected);
  const shownKey = exists(openKey);

  function showTable(name: string) {
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (name === defaultTable) next.delete("table");
        else next.set("table", name);
        return next;
      },
      { replace: true },
    );
  }

  // "Show in table": leave the issues, switch to the cell's table, make sure
  // no filter hides it, then bring it into view once it has rendered.
  function showInTable(at: CellRef) {
    onIssuesOpen(false);
    const table = tableOf(m, at.serviceId);
    if (table) showTable(table);
    if (!rows.some((r) => r.key === at.key && r.kind === at.kind)) setFilter(NO_FILTER);
    setSelected(at);
    setScrollTo(at);
  }
  useEffect(() => {
    if (!scrollTo) return;
    document
      .getElementById(cellDomId(scrollTo))
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
    setScrollTo(null);
  }, [scrollTo, active]);

  return (
    <RevealProvider canReveal={m.canReveal}>
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary">
        <span>
          {m.services.length} services · {m.rows.length} keys
        </span>
        <button
          type="button"
          onClick={() => onIssuesOpen(true)}
          className="inline-flex flex-wrap gap-x-3 hover:underline"
        >
          <span className={broken ? "font-medium text-danger" : ""}>
            · {broken} broken {broken === 1 ? "rule" : "rules"}
          </span>
          <span className={notices ? "text-warning" : ""}>
            · {notices} {notices === 1 ? "notice" : "notices"}
          </span>
        </button>
        <span className="text-text-muted">· read {timeAgo(m.collectedAt)}</span>
        {m.source === "fixture" && (
          <span className={`rounded-full px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider text-warning ${TINT.warning}`}>
            Example data
          </span>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <input
          id="env-filter"
          type="search"
          value={filter.query}
          onChange={(e) => setFilter({ ...filter, query: e.target.value })}
          placeholder="Filter keys"
          aria-label="Filter keys"
          autoComplete="off"
          spellCheck={false}
          className="w-64 max-w-full rounded-md border border-border bg-bg-card px-3 py-2 font-mono text-sm text-text-heading"
        />
        <label className="inline-flex cursor-pointer items-center gap-2 text-text">
          <input
            id="env-issues-only"
            type="checkbox"
            checked={filter.issuesOnly}
            onChange={(e) => setFilter({ ...filter, issuesOnly: e.target.checked })}
          />
          Issues only
        </label>
        <label className="inline-flex cursor-pointer items-center gap-2 text-text">
          <input
            id="env-differ-only"
            type="checkbox"
            checked={filter.differOnly}
            onChange={(e) => setFilter({ ...filter, differOnly: e.target.checked })}
          />
          Values differ
        </label>
      </div>

      <SetupBanner m={m} />

      <TableChips
        m={view}
        active={active}
        onPick={showTable}
        onAdd={() => setPickerOpen(true)}
        onRemoveCustom={() => {
          setCustom(null);
          if (active === CUSTOM_TABLE) showTable(m.tables[0]?.name ?? "");
        }}
      />

      <div role="tabpanel" aria-label={active}>
        <MatrixTable
          m={view}
          tableName={active}
          rows={rows}
          selected={showCell}
          onOpenKey={setOpenKey}
          onNotify={setToast}
        />
      </div>

      <Legend />

      <Modal
        open={issuesOpen}
        onClose={() => onIssuesOpen(false)}
        title={m.issues.length ? `Issues (${m.issues.length})` : "Issues"}
        wide
      >
        <IssuesList m={m} onShow={showInTable} />
      </Modal>

      <Modal
        open={!!shownKey}
        onClose={() => setOpenKey(null)}
        title={shownKey?.key ?? ""}
        wide={activeIds.length > 1}
      >
        {shownKey && <KeyDetails m={view} at={shownKey} serviceIds={activeIds} onNotify={setToast} />}
      </Modal>

      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} title="Compare services of your choice" wide>
        {pickerOpen && (
          <CustomPicker
            m={m}
            initial={view.tables.find((t) => t.name === CUSTOM_TABLE)?.serviceIds ?? []}
            onCancel={() => setPickerOpen(false)}
            onCompare={(ids) => {
              setCustom(ids);
              setPickerOpen(false);
              showTable(CUSTOM_TABLE);
            }}
          />
        )}
      </Modal>

      {toast && (
        <div
          role="status"
          className="fixed bottom-4 left-1/2 z-[60] max-w-[90vw] -translate-x-1/2 rounded-md border border-border px-3 py-2 text-sm text-text-secondary shadow-lg"
          style={{ background: "var(--color-bg-elevated)" }}
        >
          {toast}
        </div>
      )}
    </RevealProvider>
  );
}

/**
 * Setup problems — a render.yaml or rules file the portal can't read — on the
 * page itself, not tucked in the Issues modal: while they last, the tables
 * can't be grouped as the rules files say and no rule is checked, and a
 * summary reading "0 broken rules" would look like good news.
 */
function SetupBanner({ m }: { m: EnvMatrix }) {
  const setup = m.issues.filter((i) => i.kind === "config");
  if (setup.length === 0) return null;
  return (
    <div className={`mb-4 rounded-token-lg border px-4 py-3 text-sm ${TINT.warningBorder} ${TINT.warning}`}>
      <p className="font-semibold text-text-heading">
        <i className="fa-solid fa-plug-circle-exclamation mr-2 text-warning" />
        {setup.length === 1 ? "One setup problem" : `${setup.length} setup problems`}. Until{" "}
        {setup.length === 1 ? "it's" : "they're"} fixed, services may not be grouped the way the rules
        files say, and rules may go unchecked.
      </p>
      <ul className="mt-2 space-y-1.5">
        {setup.map((i) => (
          <li key={i.id} className="text-text">
            <span className="font-medium text-text-heading">{i.title}.</span> {i.detail}{" "}
            {i.fix && <span className="text-text-secondary">{i.fix}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Picks which kind of service the table shows. Each chip carries the issues
 * pointing at its cells, so a problem elsewhere is never out of sight.
 */
function TableChips({
  m,
  active,
  onPick,
  onAdd,
  onRemoveCustom,
}: {
  /** Including the custom table, when there is one. */
  m: EnvMatrix;
  active: string;
  onPick: (name: string) => void;
  onAdd: () => void;
  onRemoveCustom: () => void;
}) {
  const counts = useMemo(() => issuesByTable(m), [m]);
  const hasCustom = m.tables.some((t) => t.name === CUSTOM_TABLE);
  const chip = (on: boolean) =>
    `inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
      on
        ? "border-accent bg-accent text-on-accent"
        : "border-border bg-bg-card text-text-heading hover:bg-bg-card-hover"
    }`;
  return (
    <div role="tablist" aria-label="Services to compare" className="mb-4 flex flex-wrap gap-2">
      {m.tables.map((t) => {
        const on = t.name === active;
        const issues = counts.get(t.name);
        const isCustom = t.name === CUSTOM_TABLE;
        return (
          <span key={t.name} className="relative inline-flex">
            <button
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => onPick(t.name)}
              title={isCustom ? `Your comparison: ${t.serviceIds.length} ${t.serviceIds.length === 1 ? "service" : "services"}` : undefined}
              className={`${chip(on)} ${isCustom ? "pr-8" : ""}`}
            >
              {isCustom && <i className="fa-solid fa-sliders text-xs" />}
              {t.name}
              {issues && issues.count > 0 && (
                <span
                  title={`${issues.count} ${issues.count === 1 ? "issue" : "issues"}`}
                  className={`rounded-full bg-bg-card px-1.5 font-mono text-[11px] leading-5 ${
                    issues.broken ? "text-danger" : "text-warning"
                  }`}
                >
                  {issues.count}
                </span>
              )}
            </button>
            {isCustom && (
              <button
                type="button"
                onClick={onRemoveCustom}
                aria-label="Remove your comparison"
                title="Remove your comparison"
                className={`absolute right-1.5 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-full text-[11px] transition-colors ${
                  on ? "text-on-accent hover:bg-black/15" : "text-text-muted hover:bg-bg-secondary hover:text-text-heading"
                }`}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            )}
          </span>
        );
      })}
      <button
        type="button"
        onClick={onAdd}
        aria-label={hasCustom ? "Change your comparison" : "Compare services of your choice"}
        title={hasCustom ? "Change your comparison" : "Compare services of your choice"}
        className={`${chip(false)} px-3`}
      >
        <i className="fa-solid fa-plus text-xs" />
      </button>
    </div>
  );
}

function Legend() {
  const tag = "rounded border px-1 py-px font-mono text-[10px] leading-tight";
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-text-muted">
      <span>
        <span className="font-mono text-text">production</span> plain value
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="font-mono tracking-widest">••••••</span> secret, hover to reveal
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="font-mono text-[13px] font-semibold">=</span> same value on every service in
        the table
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className={`rounded px-1 font-mono text-[13px] font-semibold text-warning ${TINT.warning}`}>≠</span>{" "}
        the values differ, or a service doesn't have it
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className={`${tag} border-border`}>yaml</span> value set in render.yaml
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className={`${tag} border-dashed border-warning text-warning`}>not in yaml</span> on the
        service, not declared
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className={`${tag} border-dashed border-warning text-warning`}>edited</span> changed from
        render.yaml's value
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className={`rounded px-1.5 py-0.5 font-mono text-[11px] text-warning ${TINT.warning}`}>missing</span> a
        rule expects it here
      </span>
      <span>
        <span className="opacity-40">·</span> not set
      </span>
    </div>
  );
}

function NotConfigured({ missing }: { missing: string[] }) {
  return (
    <div className="rounded-token-lg border border-dashed border-border bg-bg-card/40 px-6 py-16 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-token-lg bg-accent-muted text-xl text-accent">
        <i className="fa-solid fa-table-cells" />
      </div>
      <h2 className="mt-4 font-display text-lg font-semibold text-text-heading">
        The env matrix needs setting up
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">
        Add{" "}
        {missing.map((k, i) => (
          <span key={k}>
            {i > 0 && (i === missing.length - 1 ? " and " : ", ")}
            <code className="font-mono text-xs">{k}</code>
          </span>
        ))}{" "}
        to the portal service in Render, then redeploy.
        {missing.includes("ENV_MATRIX_HMAC_KEY") &&
          " The HMAC key can be any long random string; a Blueprint sync generates one."}
      </p>
    </div>
  );
}
